import React, { useState, useRef } from 'react';
import { 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  FileText,
  Info,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Base URL consistente con el cliente Axios (`src/api.js`)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const getAuthToken = () => localStorage.getItem('authToken');
const isJsonResponse = (response) => {
  const ct = response.headers.get('content-type') || '';
  return ct.includes('application/json');
};

const BuyIntelligenceManager = ({ 
  marketplace, 
  proveedor, 
  onDataUpdated,
  tableData = [] 
}) => {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, validating, success, error
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef(null);

  // Estados para manejo de errores específicos
  const [errorDetails, setErrorDetails] = useState(null);

  /**
   * Generar y descargar tabla simplificada como CSV
   * Replica la funcionalidad de "Descargar tabla simplificada" de Streamlit
   */
  const handleDownloadTemplate = async () => {
    try {
      setIsProcessing(true);
      
      // Si tenemos datos de la tabla, usarlos para generar el CSV
      let csvData;
      
      if (tableData && tableData.length > 0) {
        // Filtrar solo las columnas que el usuario puede modificar
        const modifiableColumns = [
          'identificador',
          'sku', 
          'objetivo_venta',
          'dias_stock_meta',
          'decision_final_compra'
        ];
        
        // Crear CSV con datos actuales de la tabla
        const filteredData = tableData.map(row => {
          const filtered = {};
          modifiableColumns.forEach(col => {
            if (row[col] !== undefined) {
              filtered[col] = row[col];
            }
          });
          return filtered;
        });
        
        csvData = generateCSV(filteredData);
      } else {
        // Fallback: usar endpoint de template (privado → público si 401/403)
        const urlPriv = `${API_BASE_URL}/dashboard/buy-intelligence/download-template/${marketplace}?proveedor=${encodeURIComponent(proveedor || '')}`;
        const urlPub = `${API_BASE_URL}/dashboard/buy-intelligence/download-template-public/${marketplace}?proveedor=${encodeURIComponent(proveedor || '')}`;

        let response = await fetch(urlPriv, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });

        if (!response.ok && (response.status === 401 || response.status === 403)) {
          response = await fetch(urlPub);
        }

        if (!response.ok) {
          const txt = await response.text();
          throw new Error(txt || 'Error descargando plantilla');
        }

        csvData = await response.text();
      }
      
      // Crear y descargar archivo
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `tabla_simplificada_${marketplace}_${proveedor || 'todos'}.csv`;
      link.click();
      
      // Limpiar URL object
      setTimeout(() => URL.revokeObjectURL(link.href), 100);
      
    } catch (error) {
      console.error('Error descargando template:', error);
      setErrorDetails({
        type: 'download_error',
        message: 'Error al descargar la plantilla. Intente nuevamente.',
        details: error.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Generar CSV desde datos JavaScript
   */
  const generateCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escapar comillas y manejar valores con comas
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');
    
    return csvContent;
  };

  /**
   * Manejar selección de archivo
   */
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  /**
   * Validar archivo CSV antes del envío
   * Replica la validación de Streamlit
   */
  const handleFileUpload = async (file) => {
    try {
      setUploadState('uploading');
      setValidationResult(null);
      setUploadResult(null);
      setErrorDetails(null);

      // Validar tipo de archivo
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('El archivo debe ser un CSV');
      }

      // Crear FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('marketplace', marketplace);

      // Validar archivo
      setUploadState('validating');
      const urlPriv = `${API_BASE_URL}/dashboard/buy-intelligence/upload-csv`;
      const urlPub = `${API_BASE_URL}/dashboard/buy-intelligence/upload-csv-public`;

      let response = await fetch(urlPriv, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok && (response.status === 401 || response.status === 403)) {
        response = await fetch(urlPub, { method: 'POST', body: formData });
      }

      let result;
      if (isJsonResponse(response)) {
        result = await response.json();
      } else {
        const txt = await response.text();
        throw new Error(txt?.slice(0, 300) || 'Respuesta no JSON del servidor');
      }

      if (!response.ok || !result?.success) {
        throw new Error(result?.detail || result?.message || 'Error validando archivo');
      }

      // Mostrar resultado de validación
      setValidationResult(result);
      setUploadState('success');

    } catch (error) {
      console.error('Error validando archivo:', error);
      setUploadState('error');
      setErrorDetails({
        type: 'validation_error',
        message: 'Error al validar el archivo CSV',
        details: error.message
      });
    }
  };

  /**
   * Confirmar y guardar datos en la base de datos
   * Replica el botón "Guardar a la base de datos" de Streamlit
   */
  const handleConfirmSave = async () => {
    if (!validationResult || !fileInputRef.current.files[0]) {
      return;
    }

    try {
      setIsProcessing(true);
      setUploadResult(null);
      setErrorDetails(null);

      const file = fileInputRef.current.files[0];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('marketplace', marketplace);

      const urlPriv = `${API_BASE_URL}/dashboard/buy-intelligence/save-csv-data`;
      const urlPub = `${API_BASE_URL}/dashboard/buy-intelligence/save-csv-data-public`;

      let response = await fetch(urlPriv, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok && (response.status === 401 || response.status === 403)) {
        response = await fetch(urlPub, { method: 'POST', body: formData });
      }

      let result;
      if (isJsonResponse(response)) {
        result = await response.json();
      } else {
        const txt = await response.text();
        throw new Error(txt?.slice(0, 300) || 'Respuesta no JSON del servidor');
      }

      if (!response.ok || !result?.success) {
        throw new Error(result?.detail || result?.message || 'Error guardando datos');
      }

      setUploadResult(result);
      
      // Notificar al componente padre que los datos se actualizaron
      if (onDataUpdated) {
        // Pequeño delay para que el usuario vea el mensaje de éxito antes del refresh
        setTimeout(() => {
          onDataUpdated();
        }, 500);
      }

    } catch (error) {
      console.error('Error guardando datos:', error);
      setErrorDetails({
        type: 'save_error',
        message: 'Error al guardar los datos en la base de datos',
        details: error.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Resetear el componente
   */
  const handleReset = () => {
    setUploadState('idle');
    setValidationResult(null);
    setUploadResult(null);
    setErrorDetails(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Renderizar acordeón colapsable principal
   */
  const renderAccordionHeader = () => {
    const hasActivity = uploadState !== 'idle' || validationResult || uploadResult || errorDetails;
    
    return (
      <div 
        className="cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="icon-container">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="card-title">
                  Cargar tabla simplificada con datos actualizados
                </h3>
                <p className="card-subtitle">
                  Gestionar variables de buy intelligence para {marketplace}
                  {proveedor && ` • ${proveedor}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {hasActivity && (
                <div className="status-indicator">
                  {uploadState === 'uploading' || uploadState === 'validating' || isProcessing ? (
                    <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                  ) : uploadResult ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : errorDetails ? (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  ) : validationResult ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : null}
                </div>
              )}
              
              <button className="expand-button">
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renderizar contenido expandido
   */
  const renderExpandedContent = () => {
    if (!isExpanded) return null;

    return (
      <div className="accordion-content">
        {/* Instrucciones rápidas */}
        <div className="instructions-banner">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                Instrucciones rápidas
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                1. Descargue la tabla • 2. Modifique las columnas <code>objetivo_venta</code>, <code>dias_stock_meta</code>, <code>decision_final_compra</code> • 3. Suba el archivo
              </p>
            </div>
          </div>
        </div>

        {/* Grid de acciones principales */}
        <div className="actions-grid">
          {/* Descargar */}
          <div className="action-card">
            <div className="action-header">
              <div className="action-icon">
                <Download className="w-5 h-5 text-blue-500" />
              </div>
              <h4 className="action-title">1. Descargar plantilla</h4>
            </div>
            <p className="action-description">
              Obtener tabla actual con variables modificables
            </p>
            <button
              onClick={handleDownloadTemplate}
              disabled={isProcessing}
              className="btn-primary"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Descargar CSV
                </>
              )}
            </button>
          </div>

          {/* Cargar */}
          <div className="action-card">
            <div className="action-header">
              <div className="action-icon">
                <Upload className="w-5 h-5 text-green-500" />
              </div>
              <h4 className="action-title">2. Cargar archivo modificado</h4>
            </div>
            <p className="action-description">
              Seleccionar CSV con datos actualizados
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className={`btn-success ${uploadState === 'uploading' || uploadState === 'validating' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadState === 'uploading' ? 'Cargando...' : 
               uploadState === 'validating' ? 'Validando...' : 
               'Seleccionar archivo'}
            </label>
            
            {(uploadState === 'uploading' || uploadState === 'validating') && (
              <div className="loading-state">
                <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {uploadState === 'uploading' ? 'Cargando archivo...' : 'Validando estructura...'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Resultados */}
        {renderResults()}
      </div>
    );
  };

  /**
   * Renderizar todos los resultados (validación, guardado, errores)
   */
  const renderResults = () => {
    if (!validationResult && !uploadResult && !errorDetails) return null;

    return (
      <div className="results-section">
        {/* Error */}
        {errorDetails && (
          <div className="alert alert-error">
            <div className="alert-content">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="alert-title">Error en el proceso</h4>
                <p className="alert-message">{errorDetails.message}</p>
                {errorDetails.details && (
                  <div className="alert-details">
                    <code className="text-xs">{errorDetails.details}</code>
                  </div>
                )}
                <div className="alert-actions">
                  <button onClick={() => setErrorDetails(null)} className="btn-small btn-danger">
                    <X className="w-4 h-4 mr-1" />
                    Cerrar
                  </button>
                  <button onClick={handleReset} className="btn-small btn-secondary">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Reintentar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Validación exitosa */}
        {validationResult && !uploadResult && (
          <div className="alert alert-success">
            <div className="alert-content">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="alert-title">Archivo válido - Listo para procesar</h4>
                
                <div className="validation-stats">
                  <div className="stat-item">
                    <span className="stat-label">Archivo:</span>
                    <span className="stat-value">{validationResult.stats.filename}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Registros:</span>
                    <span className="stat-value">{validationResult.stats.total_rows}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Identificadores únicos:</span>
                    <span className="stat-value">{validationResult.stats.unique_identifiers}</span>
                  </div>
                </div>

                {validationResult.validation_warnings && validationResult.validation_warnings.length > 0 && (
                  <div className="warnings-section">
                    <h5 className="warnings-title">⚠️ Advertencias:</h5>
                    <ul className="warnings-list">
                      {validationResult.validation_warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="alert-actions">
                  <button
                    onClick={handleConfirmSave}
                    disabled={isProcessing}
                    className="btn-success"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Guardar en base de datos
                      </>
                    )}
                  </button>
                  <button onClick={handleReset} className="btn-secondary">
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guardado exitoso */}
        {uploadResult && (
          <div className="alert alert-success-enhanced">
            <div className="alert-content">
              <div className="success-icon-container">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="alert-title-success">🎉 Datos guardados exitosamente</h4>
                <p className="alert-message-success">{uploadResult.message}</p>
                
                {uploadResult.stats && (
                  <div className="upload-stats-enhanced">
                    <div className="stat-item-enhanced">
                      <span className="stat-label-enhanced">Registros procesados:</span>
                      <span className="stat-value-enhanced">{uploadResult.stats.total_processed}</span>
                    </div>
                    <div className="stat-item-enhanced">
                      <span className="stat-label-enhanced">Marketplace:</span>
                      <span className="stat-value-enhanced">{uploadResult.stats.marketplace}</span>
                    </div>
                  </div>
                )}

                <div className="alert-actions-enhanced">
                  <button
                    onClick={() => {
                      if (onDataUpdated) {
                        onDataUpdated();
                      }
                      handleReset();
                    }}
                    className="btn-success-primary"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refrescar tabla
                  </button>
                  <button onClick={handleReset} className="btn-success-secondary">
                    <Upload className="w-4 h-4 mr-2" />
                    Cargar otro archivo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };



  return (
    <>
      <div className="buy-intelligence-manager">
        {renderAccordionHeader()}
        {renderExpandedContent()}
      </div>
      
      <style jsx>{`
        .buy-intelligence-manager {
          margin: 24px 0;
        }

        .card {
          background: var(--color-white);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .icon-container {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #29B5E8, #56C4ED);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .icon-container svg {
          color: white;
        }

        .card-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--color-black);
          margin: 0;
          line-height: 1.3;
        }

        .card-subtitle {
          font-size: 14px;
          color: #6b7280;
          margin: 4px 0 0 0;
          line-height: 1.4;
        }

        .status-indicator {
          padding: 8px;
          border-radius: 50px;
          background: #f3f4f6;
        }

        .expand-button {
          padding: 8px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .expand-button:hover {
          background: #f3f4f6;
        }

        .accordion-content {
          margin-top: 24px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .instructions-banner {
          background: linear-gradient(135deg, #29B5E8, #56C4ED);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          color: white;
        }

        .instructions-banner code {
          background: rgba(255, 255, 255, 0.2);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'SF Mono', Consolas, monospace;
          font-size: 12px;
        }

        .actions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }

        @media (max-width: 768px) {
          .actions-grid {
            grid-template-columns: 1fr;
          }
        }

        .action-card {
          background: var(--color-white);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          transition: all 0.2s ease;
        }

        .action-card:hover {
          border-color: #29B5E8;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .action-header {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
        }

        .action-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
        }

        .action-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--color-black);
          margin: 0;
        }

        .action-description {
          color: #6b7280;
          font-size: 14px;
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .btn-primary, .btn-success, .btn-secondary, .btn-danger {
          display: inline-flex;
          align-items: center;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
          width: 100%;
          justify-content: center;
        }

        .btn-primary {
          background: #29B5E8;
          color: white;
        }

        .btn-primary:hover {
          background: #1B96C7;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-success {
          background: #38C172;
          color: white;
        }

        .btn-success:hover {
          background: #22a55d;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-secondary {
          background: transparent;
          color: #29B5E8;
          border: 2px solid #29B5E8;
        }

        .btn-secondary:hover {
          background: #29B5E8;
          color: white;
        }

        .btn-danger {
          background: #FF6B35;
          color: white;
        }

        .btn-danger:hover {
          background: #e53e3e;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-small {
          padding: 8px 16px;
          font-size: 12px;
          width: auto;
        }

        .loading-state {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 16px;
          padding: 16px;
          background: #f3f4f6;
          border-radius: 8px;
        }

        .results-section {
          margin-top: 32px;
        }

        .alert {
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
        }

        .alert-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }

        .alert-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
        }

        .alert-success-enhanced {
          background: linear-gradient(135deg, #10b981, #34d399);
          border: 1px solid #10b981;
          box-shadow: 0 4px 20px rgba(16, 185, 129, 0.25);
        }

        .alert-content {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .alert-title {
          font-weight: 600;
          color: var(--color-black);
          margin: 0 0 8px 0;
        }

        .alert-message {
          color: #374151;
          margin: 0 0 16px 0;
          line-height: 1.5;
        }

        .alert-details {
          background: rgba(0, 0, 0, 0.05);
          padding: 16px;
          border-radius: 8px;
          margin: 16px 0;
        }

        .alert-actions {
          display: flex;
          gap: 16px;
          margin-top: 24px;
        }

        .validation-stats, .upload-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin: 24px 0;
          padding: 24px;
          background: rgba(0, 0, 0, 0.02);
          border-radius: 8px;
        }

        .success-icon-container {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .alert-title-success {
          font-weight: 700;
          color: white;
          margin: 0 0 8px 0;
          font-size: 18px;
        }

        .alert-message-success {
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 16px 0;
          line-height: 1.5;
          font-size: 14px;
        }

        .upload-stats-enhanced {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin: 24px 0;
          padding: 20px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .stat-item-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .stat-label-enhanced {
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
        }

        .stat-value-enhanced {
          font-family: 'SF Mono', Consolas, monospace;
          font-size: 14px;
          color: white;
          font-weight: 600;
        }

        .alert-actions-enhanced {
          display: flex;
          gap: 16px;
          margin-top: 24px;
        }

        .btn-success-primary {
          display: inline-flex;
          align-items: center;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
          background: white;
          color: #10b981;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .btn-success-primary:hover {
          background: #f9fafb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-success-secondary {
          display: inline-flex;
          align-items: center;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
          border: 2px solid rgba(255, 255, 255, 0.3);
          cursor: pointer;
          background: transparent;
          color: white;
        }

        .btn-success-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .stat-label {
          font-weight: 500;
          color: #374151;
        }

        .stat-value {
          font-family: 'SF Mono', Consolas, monospace;
          font-size: 14px;
          color: var(--color-black);
        }

        .warnings-section {
          margin: 24px 0;
          padding: 24px;
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 8px;
        }

        .warnings-title {
          font-weight: 600;
          color: #92400e;
          margin: 0 0 8px 0;
        }

        .warnings-list {
          margin: 0;
          padding-left: 24px;
          color: #92400e;
        }

        .warnings-list li {
          margin-bottom: 4px;
          font-size: 14px;
        }

        /* Dark mode */
        [data-theme="dark"] .card {
          background: #1a1f2e;
          border-color: #374151;
        }

        [data-theme="dark"] .card-title {
          color: #f6f8fb;
        }

        [data-theme="dark"] .card-subtitle {
          color: #9aa8bd;
        }

        [data-theme="dark"] .action-card {
          background: #1a1f2e;
          border-color: #374151;
        }

        [data-theme="dark"] .action-title {
          color: #f6f8fb;
        }

        [data-theme="dark"] .action-description {
          color: #9aa8bd;
        }

        [data-theme="dark"] .action-icon {
          background: #374151;
        }

        [data-theme="dark"] .loading-state {
          background: #374151;
        }

        [data-theme="dark"] .alert-error {
          background: #431f20;
          border-color: #7f1d1d;
        }

        [data-theme="dark"] .alert-success {
          background: #14532d;
          border-color: #166534;
        }

        [data-theme="dark"] .alert-info {
          background: #1e3a8a;
          border-color: #1d4ed8;
        }

        [data-theme="dark"] .validation-stats,
        [data-theme="dark"] .upload-stats {
          background: rgba(255, 255, 255, 0.05);
        }

        [data-theme="dark"] .warnings-section {
          background: #451a03;
          border-color: #92400e;
        }

        [data-theme="dark"] .expand-button:hover {
          background: #374151;
        }

        [data-theme="dark"] .status-indicator {
          background: #374151;
        }

        /* Dark mode para estilos del mensaje de éxito mejorado */
        [data-theme="dark"] .alert-success-enhanced {
          background: linear-gradient(135deg, #059669, #10b981);
          border: 1px solid #059669;
          box-shadow: 0 4px 20px rgba(5, 150, 105, 0.3);
        }

        [data-theme="dark"] .success-icon-container {
          background: rgba(255, 255, 255, 0.15);
        }

        [data-theme="dark"] .upload-stats-enhanced {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.15);
        }

        [data-theme="dark"] .btn-success-primary {
          background: #f6f8fb;
          color: #059669;
        }

        [data-theme="dark"] .btn-success-primary:hover {
          background: #e5e7eb;
          color: #047857;
        }

        [data-theme="dark"] .btn-success-secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.6);
        }
      `}</style>
    </>
  );
};

export default BuyIntelligenceManager;
